import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Alert, ModalPortal } from '../../components/ui';
import { profileShareService } from '../../services/profileShareService';
import {
  SHARE_EXPIRY_OPTIONS,
  DEFAULT_SHARE_EXPIRY_DAYS,
  getShareExpiryLabel,
  buildShareUrl,
  openWhatsAppShare,
  PUBLIC_PASSPORT_FIELD_OPTIONS,
  DEFAULT_PUBLIC_PASSPORT_FIELDS,
} from '../../utils';
import type {
  Profile,
  WorkExperience,
  Qualification,
  ProfessionalReference,
  ShareableField,
  Document,
} from '../../types';

interface ShareOption {
  field: ShareableField;
  label: string;
  description: string;
}

const SHARE_OPTIONS: ShareOption[] = PUBLIC_PASSPORT_FIELD_OPTIONS;
const DEFAULT_SELECTED: ShareableField[] = DEFAULT_PUBLIC_PASSPORT_FIELDS;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  profile: Profile | null;
  workExperience: WorkExperience[];
  qualifications: Qualification[];
  references: ProfessionalReference[];
  documents?: Document[];
}

export const ShareProfileModal: React.FC<Props> = ({
  isOpen,
  onClose,
  userId,
  profile,
  workExperience,
  qualifications,
  references,
  documents = [],
}) => {
  const [selected, setSelected] = useState<Set<ShareableField>>(new Set(DEFAULT_SELECTED));
  const [expiresInDays, setExpiresInDays] = useState(DEFAULT_SHARE_EXPIRY_DAYS);
  const [generating, setGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [expiryLabel, setExpiryLabel] = useState(getShareExpiryLabel(DEFAULT_SHARE_EXPIRY_DAYS));
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const displayName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ');

  const toggle = (field: ShareableField) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  const handleGenerate = async () => {
    if (selected.size === 0) {
      setError('Please select at least one field to share.');
      return;
    }
    setError(null);
    setGenerating(true);

    const officialDocs = documents
      .filter(d => d.document_type === 'id_document' || d.document_type === 'police_clearance')
      .map(({ id, document_type, file_name, file_url, mime_type, is_verified, verification_status }) => ({
        id, document_type, file_name, file_url, mime_type, is_verified, verification_status,
      }));

    const snapshot = {
      profile: profile ?? {},
      work_experience: workExperience,
      qualifications,
      references,
      documents: officialDocs,
    };

    const { data, error: createError } = await profileShareService.createShare({
      userId,
      selectedFields: Array.from(selected),
      snapshot,
      expiresInDays,
    });

    setGenerating(false);

    if (createError || !data) {
      setError(createError ?? 'Failed to generate link. Please try again.');
      return;
    }

    const url = buildShareUrl(data.token);
    setGeneratedLink(url);
    setExpiryLabel(getShareExpiryLabel(expiresInDays));
  };

  const handleCopy = async () => {
    if (!generatedLink) return;
    await navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsApp = () => {
    if (!generatedLink) return;
    openWhatsAppShare(generatedLink, displayName ? `${displayName}'s` : 'my');
  };

  const handleClose = () => {
    setGeneratedLink(null);
    setSelected(new Set(DEFAULT_SELECTED));
    setExpiresInDays(DEFAULT_SHARE_EXPIRY_DAYS);
    setExpiryLabel(getShareExpiryLabel(DEFAULT_SHARE_EXPIRY_DAYS));
    setError(null);
    onClose();
  };

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 pt-[max(1rem,var(--app-safe-top,env(safe-area-inset-top,0px)))] pb-[max(1rem,var(--app-safe-bottom,env(safe-area-inset-bottom,0px)))]">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 sm:px-6 sm:pt-6">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
              <svg className="w-4.5 h-4.5 text-brand-600" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900 leading-snug">Share Your Passport</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {generatedLink ? `Expires in ${expiryLabel}` : 'Choose fields and link duration'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-5 space-y-5 sm:px-6">
          {!generatedLink ? (
            <>
              <p className="text-sm text-gray-500">
                Choose what information to include. Anyone with the link can view the selected details.
              </p>

              {error && <Alert variant="danger" onClose={() => setError(null)}>{error}</Alert>}

              <div className="space-y-2">
                {SHARE_OPTIONS.map(option => (
                  <label
                    key={option.field}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      selected.has(option.field)
                        ? 'bg-brand-50 border-brand-200'
                        : 'bg-gray-50 border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        checked={selected.has(option.field)}
                        onChange={() => toggle(option.field)}
                        className="h-4 w-4 text-brand-600 rounded border-gray-300 focus:ring-brand-500"
                      />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${selected.has(option.field) ? 'text-brand-800' : 'text-gray-700'}`}>
                        {option.label}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{option.description}</p>
                    </div>
                  </label>
                ))}
              </div>

              <div>
                <label htmlFor="share-expiry" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Link expires after
                </label>
                <select
                  id="share-expiry"
                  value={expiresInDays}
                  onChange={e => setExpiresInDays(Number(e.target.value))}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  {SHARE_EXPIRY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button onClick={handleGenerate} loading={generating} fullWidth>
                  Generate Link
                </Button>
                <Button variant="secondary" onClick={handleClose} fullWidth>
                  Cancel
                </Button>
              </div>

              <p className="text-center text-xs text-gray-400">
                <Link to="/profile/shares" onClick={handleClose} className="text-brand-600 hover:text-brand-700 font-medium">
                  Manage existing share links
                </Link>
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-emerald-800 font-medium">Link generated — expires in {expiryLabel}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Shareable Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={generatedLink}
                    className="flex-1 px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 min-w-0 focus:outline-none"
                    onClick={e => (e.target as HTMLInputElement).select()}
                  />
                  <Button onClick={handleCopy} variant={copied ? 'success' : 'secondary'} size="sm" className="flex-shrink-0">
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={handleWhatsApp} fullWidth className="!bg-[#25D366] hover:!bg-[#1ebe57] !border-transparent">
                  <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Share to WhatsApp
                </Button>
                <Button onClick={handleClose} variant="secondary" fullWidth>Done</Button>
              </div>

              <p className="text-xs text-gray-400 leading-relaxed">
                Sharing: {Array.from(selected).map(f => SHARE_OPTIONS.find(o => o.field === f)?.label).filter(Boolean).join(', ')}.
              </p>

              <p className="text-center text-xs text-gray-400">
                <Link to="/profile/shares" onClick={handleClose} className="text-brand-600 hover:text-brand-700 font-medium">
                  View all share links
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};
